/** Phase 3A.1 — Provenance drawer for AI-generated assets. */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { api } from "../../lib/api";
import AIRunBadge from "./AIRunBadge";
import { useT } from "../../lib/i18n";

const FIELDS = [
  "ai_generated", "source_agent_id", "source_agent_run_id",
  "source_campaign_id", "source_prompt_key", "source_prompt_version",
  "source_llm_provider", "source_llm_model", "source_llm_model_version",
  "generation_cost_usd", "generation_latency_ms", "agent_confidence",
  "quality_score", "safety_verdict", "framework", "platform",
];

export default function ProvenanceDrawer({ assetId, open, onClose }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !assetId) { setData(null); return; }
    setLoading(true);
    api.get(`/assets/${assetId}/provenance`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [assetId, open]);

  const fmtVal = (k, v) => {
    if (v == null) return <span className="text-muted-foreground">—</span>;
    if (typeof v === "boolean") return v ? "true" : "false";
    if (typeof v === "object") return <code className="text-[11px]">{JSON.stringify(v)}</code>;
    if (k === "generation_cost_usd") return `$${Number(v).toFixed(4)}`;
    if (k === "generation_latency_ms") return `${v}ms`;
    if (k === "agent_confidence" || k === "quality_score") return Number(v).toFixed(3);
    return String(v);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="provenance-drawer" className="overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t("provenance.title")}</SheetTitle>
        </SheetHeader>
        {loading && <div className="text-sm text-muted-foreground mt-4">{t("common.loading")}</div>}
        {data && (
          <div className="mt-4 space-y-4">
            <AIRunBadge
              model={data.source_llm_model}
              cost_usd={data.generation_cost_usd}
              latency_ms={data.generation_latency_ms}
              quality_score={data.quality_score}
            />
            <div className="rounded-md border divide-y" data-testid="provenance-fields">
              {FIELDS.map((k) => (
                <div key={k} className="grid grid-cols-2 gap-2 px-3 py-2 text-xs"
                     data-testid={`prov-row-${k}`}>
                  <span className="text-muted-foreground font-mono">{k}</span>
                  <span className="font-mono break-all">{fmtVal(k, data[k])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!loading && !data && (
          <div className="text-sm text-muted-foreground mt-4">No provenance data.</div>
        )}
      </SheetContent>
    </Sheet>
  );
}
